export type ThemeMode = 'light' | 'dark';

export interface ThemeColors {
  // Primary brand colors
  primary: string;
  primaryHover: string;
  secondary: string;
  accent: string;

  // Semantic colors
  success: string;
  warning: string;
  error: string;
  info: string;

  // Background variations
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;
  backgroundLight: string;
  backgroundLighter: string;
  backgroundSemi: string;

  // Text variations
  text: string;
  textSecondary: string;
  textMuted: string;
  textDark: string;

  // UI elements
  border: string;
  borderBright: string;
  inputBg: string;
  inputBorder: string;
  cardBackground: string;
  cardBorder: string;

  // Legacy colors (for compatibility)
  blue: string;
  red: string;
  green: string;
  darkGreen: string;
}

export interface ThemeSpacing {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

export interface ThemeTypography {
  fonts: {
    mono: string;
    sans: string;
    serif: string;
  };
  fontSizes: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
  };
  fontWeights: {
    light: number;
    normal: number;
    medium: number;
    semibold: number;
    bold: number;
  };
  lineHeights: {
    tight: number;
    normal: number;
    relaxed: number;
  };
}

export interface ThemeBorderRadius {
  none: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  full: string;
}

export interface ThemeShadows {
  none: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
  hacker: string;
}

export interface ThemeBreakpoints {
  mobile: string;
  tablet: string;
  desktop: string;
  wide: string;
}

export interface UnifiedTheme {
  mode: ThemeMode;
  colors: ThemeColors;
  spacing: ThemeSpacing;
  typography: ThemeTypography;
  borderRadius: ThemeBorderRadius;
  shadows: ThemeShadows;
  breakpoints: ThemeBreakpoints;
}

// Helper function to adjust color brightness
function adjustColor(color: string, amount: number): string {
  try {
    color = color.replace(/^#/, '');
    let r = parseInt(color.substring(0, 2), 16);
    let g = parseInt(color.substring(2, 4), 16);
    let b = parseInt(color.substring(4, 6), 16);

    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  } catch (e) {
    return color;
  }
}

// Base theme values
const baseTheme = {
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  typography: {
    fonts: {
      mono: '"Courier New", monospace',
      sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Inter", "Helvetica Neue", Arial, sans-serif',
      serif: 'Georgia, "Times New Roman", Times, serif',
    },
    fontSizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '2rem',
    },
    fontWeights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeights: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  borderRadius: {
    none: '0',
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '12px',
    full: '50%',
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1280px',
    wide: '1536px',
  },
};

// Dark theme (hacker/terminal theme)
const darkTheme: UnifiedTheme = {
  mode: 'dark',
  colors: {
    // Primary brand colors (hacker green theme)
    primary: '#1FCF5E',
    primaryHover: '#17b350',
    secondary: '#4caf50',
    accent: '#2196f3',

    // Semantic colors
    success: '#1FCF5E',
    warning: '#ffc107',
    error: '#ff0000',
    info: '#2196f3',

    // Background variations
    background: '#000000',
    backgroundSecondary: '#0a0a0a',
    backgroundTertiary: '#141414',
    backgroundLight: 'rgba(0, 20, 0, 0.8)',
    backgroundLighter: 'rgba(0, 20, 0, 0.7)',
    backgroundSemi: 'rgba(0, 0, 0, 0.9)',

    // Text variations
    text: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.9)',
    textMuted: 'rgba(255, 255, 255, 0.8)',
    textDark: 'rgba(255, 255, 255, 0.6)',

    // UI elements
    border: 'rgba(31, 207, 94, 0.2)',
    borderBright: 'rgba(31, 207, 94, 0.5)',
    inputBg: 'rgba(0, 20, 0, 0.8)',
    inputBorder: 'rgba(31, 207, 94, 0.3)',
    cardBackground: 'rgba(0, 20, 0, 0.7)',
    cardBorder: 'rgba(31, 207, 94, 0.2)',

    // Legacy colors (for compatibility)
    blue: '#27B2E5',
    red: '#FF0000',
    green: '#1FCF5E',
    darkGreen: '#002b14',
  },
  shadows: {
    none: 'none',
    sm: '0 2px 5px rgba(0, 0, 0, 0.5)',
    md: '0 4px 8px rgba(0, 0, 0, 0.3)',
    lg: '0 8px 20px rgba(0, 0, 0, 0.4)',
    xl: '0 12px 30px rgba(0, 0, 0, 0.5)',
    hacker: '0 0 15px rgba(31, 207, 94, 0.2)',
  },
  ...baseTheme,
};

// Light theme
const lightTheme: UnifiedTheme = {
  mode: 'light',
  colors: {
    // Primary brand colors
    primary: '#38B2AC',
    primaryHover: '#2C9490',
    secondary: '#4caf50',
    accent: '#2196f3',

    // Semantic colors
    success: '#38B2AC',
    warning: '#ffc107',
    error: '#e53e3e',
    info: '#2196f3',

    // Background variations
    background: '#f8f9fa',
    backgroundSecondary: '#f5f5f5',
    backgroundTertiary: '#ffffff',
    backgroundLight: '#fafafa',
    backgroundLighter: '#fdfdfd',
    backgroundSemi: 'rgba(255, 255, 255, 0.9)',

    // Text variations
    text: '#333333',
    textSecondary: '#666666',
    textMuted: '#888888',
    textDark: '#aaaaaa',

    // UI elements
    border: '#e2e8f0',
    borderBright: '#cbd5e0',
    inputBg: '#ffffff',
    inputBorder: '#e2e8f0',
    cardBackground: '#ffffff',
    cardBorder: '#e2e8f0',

    // Legacy colors (for compatibility)
    blue: '#27B2E5',
    red: '#FF0000',
    green: '#1FCF5E',
    darkGreen: '#002b14',
  },
  shadows: {
    none: 'none',
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 8px 12px rgba(0, 0, 0, 0.1)',
    xl: '0 12px 20px rgba(0, 0, 0, 0.15)',
    hacker: '0 0 15px rgba(56, 178, 172, 0.2)',
  },
  ...baseTheme,
};

// Create theme function
export const createTheme = (
  mode: ThemeMode = 'dark',
  customColors?: Partial<ThemeColors>
): UnifiedTheme => {
  const baseTheme = mode === 'dark' ? darkTheme : lightTheme;

  if (!customColors) return baseTheme;

  return {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...customColors,
    },
  };
};

// Export default themes
export const theme = darkTheme; // Default to dark/hacker theme
export const hackerTheme = darkTheme;
export const lightAppTheme = lightTheme;

// Export typed theme for styled-components
export type Theme = UnifiedTheme;

// Utility functions for responsive design
export const media = {
  mobile: `@media (max-width: ${darkTheme.breakpoints.mobile})`,
  tablet: `@media (max-width: ${darkTheme.breakpoints.tablet})`,
  desktop: `@media (min-width: ${darkTheme.breakpoints.tablet})`,
  wide: `@media (min-width: ${darkTheme.breakpoints.desktop})`,
};

// Common scrollbar styles for modals
export const modalScrollbarStyles = {
  width: '8px',
  track: 'rgba(121, 255, 225, 0.1)',
  thumb: '#79FFE1',
  thumbHover: '#e6c84c',
  borderRadius: '4px',
};

// Common motion animation constants
export const modalAnimations = {
  transition: {
    duration: 0.15,
    ease: [0.4, 0.0, 0.2, 1] as [number, number, number, number],
  },
  fadeSlide: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  },
  expandCollapse: {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
  },
};
